import { CalendarActionTypes, CalendarActionUnion } from './actions';
import { BasicDeal } from '../../types/deal.types';
import { CalendarState } from './state';
import { MaturityAnalyticsColumnName } from '../../grid/types/column.types';
import {
  AssignmentsActionsUnion,
  AssignmentsActionTypes
} from '../assignments/actions';
import {
  CalendarFiltersActionTypes,
  CalendarFiltersActionUnion
} from '../calendar-filter/actions';

export const initialCalendarState: CalendarState = {
  deals: undefined,
  loading: false,
  loaded: false,
  weekOf: undefined,
  weekChanged: true,
  flexColumn: undefined,
  maturityAnalyticsColumn: undefined,
  selectedSavedFilterId: undefined,
  error: undefined
};

export function calendarReducer(
  state: CalendarState = initialCalendarState,
  action:
    | CalendarActionUnion
    | AssignmentsActionsUnion
    | CalendarFiltersActionUnion
): CalendarState {
  switch (action.type) {
    case CalendarActionTypes.LoadCalendar:
      // eslint-disable-next-line no-case-declarations
      const showLoading = state.weekChanged || state.deals === undefined;

      return {
        ...state,
        loading: showLoading
      };
    case CalendarActionTypes.LoadCalendarSuccess:
      return {
        ...state,
        deals: action.payload,
        loading: false,
        loaded: true,
        error: undefined,
        weekChanged: false
      };
    case CalendarActionTypes.LoadCalendarFail:
      return {
        ...state,
        deals: undefined,
        loading: false,
        loaded: false,
        error: action.payload
      };
    case CalendarActionTypes.UpdateDealsSuccess: {
      const updatedDeals =
        state.deals &&
        state.deals.map(
          (oldDeal) =>
            action.payload.find((newDeal) => newDeal.id === oldDeal.id) ||
            oldDeal
        );
      const newDeals =
        state.deals &&
        action.payload.filter(
          (deal) => !state.deals.some((oldDeal) => oldDeal.id === deal.id)
        );

      return {
        ...state,
        deals: state.deals && [...updatedDeals, ...newDeals]
      };
    }
    case CalendarActionTypes.UpdateDealsFail:
      return {
        ...state,
        error: action.payload
      };
    case CalendarActionTypes.UpdateWeekOf:
      return {
        ...state,
        weekOf: action.payload,
        weekChanged: action.payload !== state.weekOf,
        loading: action.payload !== state.weekOf || state.loading
      };
    case CalendarActionTypes.SetFlexColumn:
      return {
        ...state,
        flexColumn: action.payload
      };
    case CalendarActionTypes.SetMaturityAnalyticsColumn:
      if (!action.payload && state.maturityAnalyticsColumn) {
        return state;
      }

      return {
        ...state,
        maturityAnalyticsColumn:
          action.payload || MaturityAnalyticsColumnName.Spread
      };
    case CalendarActionTypes.RemoveMaturityAnalyticsColumn:
      return {
        ...state,
        maturityAnalyticsColumn: undefined
      };
    case AssignmentsActionTypes.AssignUsersSuccess:
      return {
        ...state,
        deals:
          state.deals &&
          state.deals.map((deal: BasicDeal) =>
            deal.id === action.payload.dealId
              ? {
                  ...deal,
                  assignedUsers: action.payload.userIds
                }
              : { ...deal }
          )
      };
    case CalendarActionTypes.SetSavedFilter:
      return {
        ...state,
        selectedSavedFilterId: action.payload && action.payload.id
      };
    case CalendarFiltersActionTypes.Set:
    case CalendarFiltersActionTypes.Reset:
      return {
        ...state,
        selectedSavedFilterId: undefined
      };
    default:
      return state;
  }
}

export const getCalendarDeals = (state: CalendarState): BasicDeal[] =>
  state.deals;

export const getCalendarDealAssignments =
  (dealId: string) => (state: CalendarState) => {
    const found = state.deals && state.deals.find((deal) => deal.id === dealId);
    return found && found.assignedUsers;
  };
export const getCalendarAssignedDeals =
  (userId: string) => (state: CalendarState) =>
    state.deals &&
    state.deals
      .filter(
        (deal) => deal.assignedUsers && deal.assignedUsers.includes(userId)
      )
      .map((deal) => deal.id);

export const getCalendarWeekOf = (state: CalendarState) => state.weekOf;

export const selectCalendarLoading = (state: CalendarState) => state.loading;

export const selectCalendarError = (state: CalendarState) => state.error;

export const selectCalendarFlexColumn = (state: CalendarState) =>
  state.flexColumn;

export const getCalendarMaturityAnalyticsColumn = (state: CalendarState) =>
  state.maturityAnalyticsColumn;
